import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, HardHat, MapPin, Layers, Clock } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { HERO_SEQUENCE, HERO_VIDEO_URL, IMAGES } from '@/data/constants';
import { MarketplaceStats, compactMoney, budgetValueNote } from '@/lib/homeStats';
import { CountUp, prefersReducedMotion } from '@/components/home/Reveal';
import RoleQuickStart from '@/components/home/RoleQuickStart';
import MemberValueStrip from '@/components/home/MemberValueStrip';
import { membershipCopy } from '@/lib/membershipValue';

interface Props {
  stats: MarketplaceStats;
  /** Anchor id of the "How it works" section to scroll to. */
  howItWorksId: string;
}

/**
 * Cinematic hero. Renders the licensed brand film when HERO_VIDEO_URL is set,
 * otherwise cross-fades the four-frame HERO_SEQUENCE with a slow Ken Burns
 * push. Both paths stop entirely under prefers-reduced-motion.
 */
const FRAME_MS = 5200;

const HeroVideo: React.FC<Props> = ({ stats, howItWorksId }) => {
  const { t, path, lang } = useI18n();
  const [frame, setFrame] = useState(0);
  const reduced = prefersReducedMotion();
  const useVideo = Boolean(HERO_VIDEO_URL);
  const animate = !useVideo && !reduced && HERO_SEQUENCE.length > 1;

  /**
   * FIRST-LOAD WEIGHT ON PHONES. Frames 2-4 (~290 KB) used to be in the DOM
   * from the start; `loading="lazy"` does not help for images inside the
   * visible hero, so phones downloaded all four before anything else.
   *  - frame2Mounted: frame 2 is inserted when the first crossfade is due.
   *  - crossfaded:    set once frame 2 has decoded and is faded in; frames
   *                   3-4 are only inserted after this.
   * Reduced-motion visitors never download frames 2-4 at all.
   */
  const [frame2Mounted, setFrame2Mounted] = useState(false);
  const [crossfaded, setCrossfaded] = useState(false);
  const loaded = useRef<Set<number>>(new Set([0]));
  const crossfadedRef = useRef(false);

  const markLoaded = useCallback((i: number) => {
    loaded.current.add(i);
    if (i === 1 && !crossfadedRef.current) {
      crossfadedRef.current = true;
      setFrame(1);
      setCrossfaded(true);
    }
  }, []);

  // Mount frame 2 when the first crossfade is due.
  useEffect(() => {
    if (!animate) return;
    const id = window.setTimeout(() => setFrame2Mounted(true), FRAME_MS);
    return () => window.clearTimeout(id);
  }, [animate]);

  // After the first crossfade, rotate — but never onto a frame that has not loaded.
  useEffect(() => {
    if (!animate || !crossfaded) return;
    const id = window.setInterval(() => {
      setFrame((f) => {
        const next = (f + 1) % HERO_SEQUENCE.length;
        return loaded.current.has(next) ? next : f;
      });
    }, FRAME_MS);
    return () => window.clearInterval(id);
  }, [animate, crossfaded]);

  const budget = compactMoney(stats.postedBudgetValue);

  // Only tiles backed by a real, computable figure are rendered. A tile with no
  // data disappears instead of being padded with an invented number.
  const tiles = [
    stats.activeProjects > 0 && {
      key: 'projects',
      icon: Layers,
      value: <CountUp value={stats.activeProjects} />,
      label: t('home.statActiveProjects'),
      note: '',
    },
    stats.statesCovered > 0 && {
      key: 'states',
      icon: MapPin,
      value: <CountUp value={stats.statesCovered} />,
      label: t('home.statStates'),
      note: '',
    },
    {
      key: 'trades',
      icon: HardHat,
      value: <CountUp value={stats.tradesCovered} />,
      label: t('home.statTrades'),
      note: '',
    },
    budget
      ? {
          key: 'budget',
          icon: Clock,
          value: budget,
          label: t('home.statPostedValue'),
          /* The budget qualifier is NOT printed inside the tile. It is shown
             once, directly beneath the stat grid, so the same sentence never
             appears twice on the page. */
          note: '',
        }
      : null,

  ].filter(Boolean) as {
    key: string;
    icon: typeof Layers;
    value: React.ReactNode;
    label: string;
    note: string;
  }[];

  return (
    <section className="relative isolate overflow-hidden bg-brand-navy">
      {/* ---------- BACKDROP ---------- */}
      <div className="absolute inset-0" aria-hidden="true">
        {useVideo ? (
          <video
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={IMAGES.hero}
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          HERO_SEQUENCE.map((f, i) => {
            /* LAZY FRAMES (2026-10-11): only frame 1 (the LCP image) is in the
               DOM on first paint. Frame 2 mounts when the first crossfade is
               due and the crossfade waits for it to decode; frames 3-4 mount
               only after that first crossfade has happened. */
            if (i === 1 && !frame2Mounted) return null;
            if (i > 1 && !crossfaded) return null;
            return (
              <img
                key={f.src}
                src={f.src}
                alt=""
                ref={(el) => {
                  if (el && el.complete && el.naturalWidth > 0) markLoaded(i);
                }}
                onLoad={() => markLoaded(i)}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding={i === 0 ? 'sync' : 'async'}
                fetchPriority={i === 0 ? 'high' : 'low'}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ease-in-out ${
                  i === frame ? 'opacity-100' : 'opacity-0'
                } ${i === frame && !reduced ? 'animate-ken-burns' : ''}`}
              />
            );
          })
        )}
      </div>

      {/* Depth: navy wash left -> transparent right, plus a warm orange glow. */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-brand-navy/45"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-brand-orange/25 blur-[130px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-[380px] w-[380px] rounded-full bg-brand-blue/20 blur-[120px]"
        aria-hidden="true"
      />

      {/* ---------- CONTENT ---------- */}
      {/* Tighter top padding on phones so the three role doors sit above the fold. */}
      <div className="relative mx-auto flex max-w-7xl flex-col justify-center px-4 pb-14 pt-7 sm:min-h-[560px] sm:px-6 sm:py-16 lg:min-h-[600px] lg:px-8">
        <span className="hidden w-fit items-center gap-2 rounded-full bg-brand-orange/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-orange-200 ring-1 ring-brand-orange/40 backdrop-blur sm:inline-flex">
          <HardHat className="h-3.5 w-3.5" /> {t('home.heroBadge')}
        </span>

        <h1 className="max-w-3xl text-[28px] font-extrabold leading-[1.1] tracking-tight text-white sm:mt-5 sm:text-5xl lg:text-6xl">
          {t('home.heroTitleV2')}
        </h1>
        {/* Hero sub no longer ends on "at no cost": it states the member
            price, derived from constants via membershipCopy(). */}
        <p className="mt-4 hidden max-w-2xl text-base leading-relaxed text-slate-200 sm:block sm:text-lg">
          {membershipCopy(lang).heroSub}
        </p>

        {/* PRIMARY ROLE DOORS — the first decision a visitor makes, one click each. */}
        <RoleQuickStart variant="hero" className="mt-5 max-w-5xl sm:mt-8" />

        {/* MEMBER VALUE — the only membership message above the fold on phones. */}
        <MemberValueStrip className="mt-4" />

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold">
          <Link to={path('/jobs')} className="inline-flex items-center gap-1.5 text-orange-200 hover:text-white">
            {t('home.ctaBrowseProjects')} <ArrowRight className="h-4 w-4" />
          </Link>
          <a href={`#${howItWorksId}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white">
            <PlayCircle className="h-4 w-4" /> {t('home.ctaHowItWorks')}
          </a>
        </div>

        {/* ---------- TRUST STATS (all derived from live listings) ----------
            Deliberately SECONDARY to the role cards: smaller numbers, muted
            glass, so nothing in the hero competes with the three doors. */}
        {tiles.length > 0 && (
          <dl className="mt-8 grid max-w-3xl grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
            {tiles.map((tile) => (
              <div
                key={tile.key}
                className="rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 backdrop-blur-sm"
              >
                <tile.icon className="h-4 w-4 text-brand-orange/80" aria-hidden="true" />
                <dd className="mt-1 text-lg font-extrabold tabular-nums text-white/90 sm:text-xl">
                  {tile.value}
                </dd>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {tile.label}
                </dt>
                {tile.note && <p className="mt-1.5 text-[11px] leading-snug text-slate-400">{tile.note}</p>}
              </div>
            ))}
          </dl>
        )}

        {/* ONE disclaimer, directly under the hero statistics. This is the only
            place on the home page where the budget qualifier and the data
            source are stated — the duplicate copies that used to sit in the
            tile and in the "marketplace at a glance" strip are gone. */}
        <p className="mt-4 max-w-2xl text-xs leading-relaxed text-slate-400">
          {t('home.statsSource')}
          {budget ? ` ${budgetValueNote(stats, lang)}` : ''}
        </p>

      </div>
    </section>
  );
};

export default HeroVideo;
